import { SupportTokenAction } from '@apeiron/config'
import { useCurrencyContext } from '@apeiron/currency'
import { DailyCheckInShop, DailyCheckInShopReward } from '@apeiron/reward'
import {
  ApeironBlueButton,
  ApeironInfinityDataGrid,
  ApeironInfinityDataGridRef,
  IconText,
  InfinityDatasourceResult,
  SkeletonImage,
  useMessageContext,
  useWeb3
} from '@apeiron/temp'
import AllInclusiveIcon from '@mui/icons-material/AllInclusive'
import { Box, styled, Typography } from '@mui/material'
import { useDailyCheckInContext } from '@src/contexts/reward/DailyCheckInContext'
import { ColDef, ICellRendererParams } from 'ag-grid-community'
import { t } from 'i18next'
import * as R from 'ramda'
import * as RA from 'ramda-adjunct'
import { FC, useCallback, useMemo, useRef } from 'react'

const MainContainer = styled(Box)`
  padding: 1%;
  gap: 20px;
`

const ColumnContainer = styled(Box)`
  flex-direction: column;
  display: flex;
  width: 100%;
  height: 100%;
  position: relative;
`

const CellContainer = styled(Box)`
  display: flex;
  gap: 16px;
  width: 100%;
  height: 100%;
  padding: 14px 14px 14px 0;
  align-items: center;
`

const IconImg = styled(SkeletonImage)`
  aspect-ratio: 1;
  height: 100%;
  position: relative;
`

const StyledApeironDataGrid = styled(ApeironInfinityDataGrid)`
  height: 386px;
  .Custom-Grid {
    .ag-header-row {
      background-color: #151615;
    }
    .ag-header-cell-text {
      padding: 0px 2px;
      text-align: center;
      white-space: pre-wrap;
    }
    .ag-row {
      border-bottom: 1px solid #ffffff33;
    }
    .ag-row-hover {
      background-color: #ffffff11;
    }
    .ag-body-viewport {
      background-color: #ffffff33;
    }
  }
  .Custom-Pagination {
    max-width: 100%;
  }
`

const RedeemButton = styled(ApeironBlueButton)`
  --background-color: #53a513;
  border-radius: 20px;
  height: unset;
  padding: 5px 20px;
`

const TitleContainer = styled(Box)`
  flex-direction: row;
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled(Typography)`
  font-size: 30px;
  font-weight: 700;
`

const TotalContainer = styled(Box)`
  align-items: center;
  display: flex;
  gap: 10px;
`

const TotalText = styled(Typography)`
  font-size: 18px;
  font-weight: 300;
`

const TotalCount = styled(IconText)`
  gap: 0px;
  .Custom-Text {
    color: #ffaa00;
    font-size: 20px;
    font-weight: 500;
    margin-left: 5px;
  }
  .Custom-Icon {
    margin-right: -3px;
    width: 24px;
  }
`

const GiftName = styled(Typography)`
  white-space: wrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledAllInclusiveIcon = styled(AllInclusiveIcon)`
  width: 18px;
`

const CheckInGiftShop: FC<Props> = (props: Props) => {
  const { className } = props

  const {
    stardust,
    displayStardust,
    dailyCheckInShopItem,
    fetchAfterOperation,
    fetchDailyCheckInShopItem,
    mutateBuyShopItem
  } = useDailyCheckInContext()

  const { setInfo } = useMessageContext()

  const { getDefaultSupportCurrency } = useCurrencyContext()

  const currency = useMemo(
    () => getDefaultSupportCurrency(SupportTokenAction.DailyCheckIn),
    [getDefaultSupportCurrency]
  )

  const { fromWei } = useWeb3()

  const gridRef = useRef<ApeironInfinityDataGridRef>(null)

  const handleOnFetch = useCallback(
    async (
      page: number,
      pageSize: number
    ): Promise<InfinityDatasourceResult> => {
      const result = await fetchDailyCheckInShopItem({
        pageInput: { page, pageSize }
      })

      return {
        data: result.data,
        total: result.pageInfo.total
      }
    },
    [fetchDailyCheckInShopItem]
  )

  const handleOnRedeemConfirm = useCallback(
    async (shop: DailyCheckInShop) => {
      const success = await mutateBuyShopItem(shop.shopItemId)

      if (success) {
        fetchAfterOperation()

        if (R.isNotNil(gridRef.current)) {
          gridRef.current.refreshData()
        }

        setInfo(null)
      }
    },
    [fetchAfterOperation, mutateBuyShopItem, setInfo]
  )

  const handleOnRedeem = useCallback(
    async (shop: DailyCheckInShop) => {
      const extractItemNames = R.pipe<any, any, any>(
        R.map((item: DailyCheckInShopReward) => {
          const showQuantity = item.quantity > 0

          const name = R.pathOr('', ['meta', 'name'], item)

          return showQuantity ? `${name} x ${item.quantity}` : name
        }),
        R.join(',')
      )

      const names = extractItemNames(shop.shopRewards)

      setInfo({
        message: t('reward.checkin.shop.redeem_item', { name: names }),
        confirm: t('common.confirm'),
        cancel: t('common.cancel'),
        onConfirm: () => handleOnRedeemConfirm(shop)
      })
    },
    [handleOnRedeemConfirm, setInfo]
  )

  const columns = useMemo((): ColDef<any, DailyCheckInShop>[] => {
    return [
      {
        field: 'start',
        headerName: '',
        pinned: 'left',
        width: 30
      },
      {
        field: 'gift',
        flex: 1,
        headerClass: 'custom-ag-header-left',
        headerName: t('adeptus.reward.checkin.gift'),
        minWidth: 180,
        cellRenderer: (params: ICellRendererParams<DailyCheckInShop>) => {
          const { data = null } = params

          if (R.isNil(data)) {
            return <></>
          }

          const disableQuantityList = ['BootyTime'] // Not showing quantity when ID in here.

          return (
            <ColumnContainer>
              {RA.mapIndexed((item: DailyCheckInShopReward, index: number) => {
                return (
                  <CellContainer key={index}>
                    {item.meta?.image !== '' && (
                      <IconImg src={item.meta?.image} />
                    )}
                    <GiftName>
                      {item.meta?.name}
                      {disableQuantityList.indexOf(item.rewardID) === -1 && (
                        <>{' x ' + item.quantity}</>
                      )}
                    </GiftName>
                  </CellContainer>
                )
              }, data.shopRewards)}
            </ColumnContainer>
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'quantity',
        flex: 1,
        headerClass: 'custom-ag-header-center',
        headerName: t('adeptus.reward.checkin.quantity'),
        minWidth: 120,
        cellRenderer: (params: ICellRendererParams<DailyCheckInShop>) => {
          const { data = null } = params

          if (R.isNil(data)) {
            return <></>
          }

          return data.totalCount === -1 ? (
            <StyledAllInclusiveIcon />
          ) : (
            data.remainCount
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'price',
        flex: 1,
        headerClass: 'custom-ag-header-center',
        headerName: t('common.price'),
        valueGetter: 'node.id',
        minWidth: 250,
        cellRenderer: (params: ICellRendererParams<DailyCheckInShop>) => {
          const { data = null } = params

          return fromWei(
            String(data?.itemCost.quantity || 0),
            currency?.decimals || 1
          )
        }
      },
      {
        cellClass: 'custom-ag-cell-center',
        field: 'redeem',
        flex: 1,
        headerClass: 'custom-ag-header-center',
        headerName: t('adeptus.reward.checkin.redeem'),
        valueGetter: 'node.id',
        minWidth: 100,
        cellRenderer: (params: ICellRendererParams<DailyCheckInShop>) => {
          const { data = null } = params

          if (R.isNil(data)) {
            return <></>
          }

          const shopItem = data as DailyCheckInShop

          const enoughBalance =
            BigInt(stardust) >= BigInt(shopItem.itemCost.quantity)

          const enoughStock =
            shopItem.totalCount === -1 || shopItem.remainCount !== 0

          return (
            <RedeemButton
              disabled={!enoughBalance || !enoughStock}
              onClick={() => handleOnRedeem(data)}
            >
              {t('adeptus.reward.checkin.redeem')}
            </RedeemButton>
          )
        }
      }
    ]
  }, [currency?.decimals, fromWei, handleOnRedeem, stardust])

  return !R.isEmpty(dailyCheckInShopItem.length) ? (
    <MainContainer className={className}>
      <TitleContainer>
        <Title>{t('adeptus.reward.checkin.gift')}</Title>
        <TotalContainer>
          <TotalText>{t('common.total')}</TotalText>
          <TotalCount startSrc={currency?.image} text={displayStardust} />
        </TotalContainer>
      </TitleContainer>
      <StyledApeironDataGrid
        columnDefs={columns}
        gridRef={gridRef}
        rowHeight={60}
        fetch={handleOnFetch}
      />
    </MainContainer>
  ) : (
    <></>
  )
}

type Props = {
  className?: string
}

export default CheckInGiftShop
